import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { BrandTheme } from '../models/brand.model';
import { FulfillmentMode } from '../models/order.model';
import { StorefrontConfig, STOREFRONT } from '../models/storefront.model';
import { StorefrontService } from './storefront.service';
import { BranchService } from './branch.service';

export type { HeroConfig, StoryConfig, LoyaltyConfig, HomeSection, StorefrontConfig } from '../models/storefront.model';
export { STOREFRONT } from '../models/storefront.model';

export type AccentName = 'Butcher' | 'Ember' | 'Emerald' | 'Charcoal' | 'Royal';

export const ACCENTS: Record<AccentName, BrandTheme> = {
  Butcher:  { brand: '#A4161A', deep: '#7A0F12', l300: '#ef6f63', ink: '#221110', display: 'Plus Jakarta Sans' },
  Ember:    { brand: '#D9381E', deep: '#AE2A14', l300: '#ff7e63', ink: '#1b1512', display: 'Plus Jakarta Sans' },
  Emerald:  { brand: '#059e54', deep: '#047a42', l300: '#5fe49b', ink: '#0e1a14', display: 'Plus Jakarta Sans' },
  Charcoal: { brand: '#3f3f46', deep: '#27272a', l300: '#a1a1aa', ink: '#18181b', display: 'Plus Jakarta Sans' },
  Royal:    { brand: '#4f46e5', deep: '#4035c4', l300: '#a5b0ff', ink: '#14131f', display: 'Plus Jakarta Sans' },
};

/**
 * Storefront configuration. Reads the published config from StorefrontService
 * (loaded from `/api/v1/brand`) and falls back to the bundled defaults.
 */
@Injectable({ providedIn: 'root' })
export class ConfigService {
  private readonly _accent = new BehaviorSubject<AccentName>('Butcher');
  readonly accent$ = this._accent.asObservable();

  constructor(private sf: StorefrontService, private branches: BranchService) {}

  /** Free-delivery threshold (from published commerce config). */
  get freeDeliveryThreshold(): number { return this.sf.brand?.freeDeliveryThreshold ?? 250; }

  /** Currency code from the published commerce config (e.g. 'EGP'). */
  get currency(): string { return this.sf.brand?.currency || 'EGP'; }

  /** Published storefront content (hero, sections, story…) or bundled defaults. */
  get storefront(): StorefrontConfig { return this.sf.storefront ?? STOREFRONT; }

  /** Whether promo/offer codes are enabled (checkout shows the promo input). */
  get offerEnabled(): boolean { return !!this.sf.commerce?.offers_enabled; }
  get maintenance(): boolean { return this.sf.maintenance; }
  /** No brand matches this address. */
  get brandNotFound(): boolean { return this.sf.notFound; }
  /** The storefront is replaced by a full-screen page (maintenance or brand not found). */
  get storeOff(): boolean { return this.sf.maintenance || this.sf.notFound; }
  get maintenanceMessage(): string { return this.sf.maintenanceMessage || ''; }
  get brandName(): string { return this.sf.brand?.name || ''; }
  get brandLogo(): string { return this.sf.brand?.logo || ''; }
  get brandDomain(): string { return this.sf.brand?.domain || ''; }
  /** Host of the brand favicon URL (no protocol), e.g. "bittersweet.admin.getsonic.co/". */
  get faviconOrigin(): string {
    const f = this.sf.favicon;
    if (!f) return '';
    try { return new URL(f).host + '/'; } catch { return ''; }
  }
  get brandHotline(): string { return this.sf.brand?.hotline || ''; }
  get brandTagline(): string { return this.sf.brand?.tagline || ''; }
  get hotlineHours(): string { return this.storefront.hotlineHours || ''; }
  get footerText(): string { return this.storefront.footerText || ''; }
  get legal() { return this.storefront.legal; }
  get social() { return this.storefront.social || []; }

  /** Reservations are offered when the operator turned the homepage section on. */
  get reservationsEnabled(): boolean {
    return this.storefront.homepage.sections.some((s) => s.id === 'reservations' && s.on);
  }

  /** Order types the storefront offers — brand `enabled_order_types` first. */
  get supportedOrderTypes(): FulfillmentMode[] {
    if (this.sf.supportedOrderTypes.length) return this.sf.supportedOrderTypes;
    const fromBranches = this.branches.supportedOrderTypes;
    return fromBranches.length ? fromBranches : ['delivery', 'pickup', 'dinein'];
  }

  /** Whether the operator has enabled the Home screen. */
  get homeEnabled(): boolean { return this.storefront.homepage.enabled !== false; }

  /** Active theme: published theme if loaded, else the selected preset. */
  get theme(): BrandTheme { return this.sf.theme ?? ACCENTS[this._accent.value]; }

  setAccent(name: AccentName): void { this._accent.next(name); }

  /**
   * CSS custom properties for the app root, given the active theme + language.
   * Bound with [ngStyle] on the .fr-root wrapper.
   */
  brandVars(isRtl: boolean): Record<string, string> {
    const th = this.theme;
    const display = isRtl ? 'Cairo' : th.display || 'Plus Jakarta Sans';
    // The brand's display font is the whole site's font (Latin). Arabic keeps Cairo —
    // the stylesheet's [dir=rtl] rule sets it, so nothing is overridden in RTL.
    const siteFont: Record<string, string> = isRtl ? {} : {
      '--fr-font': `'${display}', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
      '--fr-font-num': `'${display}', 'Inter', ui-monospace, monospace`,
    };
    return {
      ...siteFont,
      '--brand': th.brand,
      '--brand-deep': th.deep,
      '--brand-300': th.l300,
      '--brand-ink': th.ink,
      '--brand-tint': `color-mix(in srgb, ${th.brand} 11%, #fff)`,
      '--brand-shadow': `color-mix(in srgb, ${th.brand} 55%, transparent)`,
      '--brand-display': `'${display}', var(--fr-font)`,
      '--gold': '#F2A93B',
      '--gold-deep': '#B26C0E',
      '--gold-tint': 'color-mix(in srgb, #F2A93B 16%, #fff)',
      '--fr-page': '#f6f3ee',
      '--fr-surface-3': '#f1ece4',
      '--fr-border': '#ece6dd',
      '--fr-border-2': '#e4ddd2',
    };
  }
}
